import React, { useEffect, useState } from "react";
import { Alert, Button, Table, Modal, Form, Badge, Row, Col } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";

const TIPURI_SERII = ["Factura", "Aviz", "Proforma", "Bon Fiscal", "Bon transfer", "Bon consum"];

export default function SeriiConfig() {
  const [serii, setSerii] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [gestiuni, setGestiuni] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const stockSeries = ["Bon transfer", "Bon consum"].includes(formData.tip_serie);
  
  const fetchSerii = async () => {
    try {
      const data = await authFetch("/serii");
      setSerii(data.items || []);
    } catch (e) {
      setError(e.message || "Nu s-au putut încărca seriile.");
    }
  };

  useEffect(() => {
    fetchSerii();
    async function loadGestiuni() {
      try {
        const items = [];
        let skip = 0;
        while (true) {
          const data = await authFetch(`/bxgestiune?skip=${skip}&limit=100`);
          const batch = data.items || [];
          items.push(...batch);
          if (!batch.length || items.length >= (data.total ?? items.length)) break;
          skip += batch.length;
        }
        setGestiuni(items);
      } catch (e) {
        setError(e.message || "Nu s-au putut încărca gestiunile.");
      }
    }
    loadGestiuni();
  }, []);

  const handleEdit = (s) => {
    setError("");
    setFormData(s);
    setShowModal(true);
  };

  const handleNew = () => {
    setError("");
    setFormData({ tip_serie: "Factura", numar_start: 1, numar_curent: 1, is_default: false, gestiune_id: null });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const payload = { ...formData, gestiune_id: stockSeries ? (formData.gestiune_id || null) : null };
    try {
      if (formData.id) {
        await authFetch(`/serii/${formData.id}`, { method: "PUT", body: JSON.stringify(payload) });
      } else {
        await authFetch("/serii", { method: "POST", body: JSON.stringify(payload) });
      }
      setShowModal(false);
      fetchSerii();
    } catch (e) {
      setError(e.message || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sigur stergeti?")) return;
    try {
      await authFetch(`/serii/${id}`, { method: "DELETE" });
      fetchSerii();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between mb-3">
        <h5 className="mb-0 text-primary fw-bold">Serii Documente</h5>
        <Button size="sm" variant="success" onClick={handleNew}>+ Adauga Serie Noua</Button>
      </div>
      {error && !showModal && <Alert variant="danger">{error}</Alert>}
      <Table bordered hover size="sm" className="bg-white shadow-sm">
        <thead className="table-light">
          <tr>
            <th>Tip Document</th>
            <th>Serie</th>
            <th>Gestiune</th>
            <th>Descriere</th>
            <th>Start</th>
            <th>Numărul următor</th>
            <th>Implicit</th>
            <th>Actiuni</th>
          </tr>
        </thead>
        <tbody>
          {serii.map(s => (
            <tr key={s.id}>
              <td>{s.tip_serie}</td>
              <td className="fw-bold">{s.serie}</td>
              <td>{s.gestiune_id ? (gestiuni.find(g => g.id === s.gestiune_id)?.bxnume || `Gestiunea ${s.gestiune_id}`) : "Toată firma"}</td>
              <td>{s.descriere}</td>
              <td>{s.numar_start}</td>
              <td>{s.numar_curent}</td>
              <td>{s.is_default && <Badge bg="success">DA</Badge>}</td>
              <td>
                <Button size="sm" variant="outline-primary" className="me-2 py-0 px-2" onClick={() => handleEdit(s)}>Edit</Button>
                <Button size="sm" variant="outline-danger" className="py-0 px-2" onClick={() => handleDelete(s.id)}>Sterge</Button>
              </td>
            </tr>
          ))}
          {serii.length === 0 && <tr><td colSpan={8} className="text-center text-muted">Nu exista serii definite.</td></tr>}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton><Modal.Title>{formData.id ? "Editare Serie" : "Adauga Serie"}</Modal.Title></Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="mb-2" controlId="serie-tip">
              <Form.Label>Tip Document</Form.Label>
              <Form.Select value={formData.tip_serie || ""} onChange={e => setFormData({...formData, tip_serie: e.target.value, gestiune_id: null})} required>
                {TIPURI_SERII.map(t => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </Form.Group>
            {stockSeries && <Form.Group className="mb-2" controlId="serie-gestiune">
              <Form.Label>Gestiune</Form.Label>
              <Form.Select value={formData.gestiune_id || ""} onChange={e => setFormData({...formData, gestiune_id: e.target.value ? Number(e.target.value) : null})}>
                <option value="">Toată firma (implicit pentru gestiunile fără serie proprie)</option>
                {gestiuni.map(g => <option key={g.id} value={g.id}>{g.bxcod} — {g.bxnume}</option>)}
              </Form.Select>
              <Form.Text>La transfer se folosește gestiunea sursă; la consum, gestiunea din care se consumă.</Form.Text>
            </Form.Group>}
            <Form.Group className="mb-2" controlId="serie-cod">
              <Form.Label>Serie (ex: FCT)</Form.Label>
              <Form.Control type="text" value={formData.serie || ""} onChange={e => setFormData({...formData, serie: e.target.value.toUpperCase()})} required maxLength={15}/>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Descriere (ex: Facturi RO 2024)</Form.Label>
              <Form.Control type="text" value={formData.descriere || ""} onChange={e => setFormData({...formData, descriere: e.target.value})} />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-2" controlId="serie-start">
                  <Form.Label>Numar Start</Form.Label>
                  <Form.Control type="number" min="1" value={formData.numar_start || 1} onChange={e => setFormData({...formData, numar_start: parseInt(e.target.value)})} required/>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-2" controlId="serie-urmator">
                  <Form.Label>Numărul următor</Form.Label>
                  <Form.Control type="number" min={formData.numar_start || 1} value={formData.numar_curent || 1} onChange={e => setFormData({...formData, numar_curent: parseInt(e.target.value)})} required/>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mt-3">
              <Form.Check id="serie-implicit" type="checkbox" label="Serie implicită pentru acest tip și gestiune" checked={formData.is_default || false} onChange={e => setFormData({...formData, is_default: e.target.checked})} />
            </Form.Group>
            {stockSeries && <p className="small text-muted mt-2 mb-0">Bifează seria implicită pentru numerotare automată. Numărul se alocă la salvarea bonului, inclusiv pentru bonurile generate din deviz. Exemplu: {formData.serie || "BT"}-{formData.numar_curent || 1}.</p>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Anuleaza</Button>
            <Button variant="primary" type="submit" disabled={saving}>{saving ? "Se salvează..." : "Salveaza"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

